import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { MessageSquare, Mail, Loader2, Plus, Pencil, Trash2, Send, Clock, CheckCircle2, Smartphone, MailCheck } from "lucide-react";
import { isQuillEmpty, toEditorHtml } from "@/components/RichTextEditor";
import EmailFields from "@/components/comm/EmailFields";
import WhatsAppFields from "@/components/comm/WhatsAppFields";
import { TestSendMenu } from "@/components/comm/TestSendBlock";
import { BulkProgress } from "@/components/comm/SendLog";

const STATUS = {
  scheduled: { label: "Gepland", cls: "border-slate-200 bg-slate-50 text-slate-600" },
  awaiting_approval: { label: "Wacht op goedkeuring", cls: "border-amber-300 bg-amber-50 text-amber-700" },
  sending: { label: "Wordt verzonden…", cls: "border-blue-200 bg-blue-50 text-blue-700" },
  sent: { label: "Verzonden", cls: "border-emerald-200 bg-emerald-50 text-emerald-700" },
  skipped: { label: "Niet verstuurd", cls: "border-slate-200 bg-slate-100 text-slate-400" },
};

const fmtDue = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("nl-NL", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

export function SendStatusDialog({ jobId, title, open, onClose }) {
  const [job, setJob] = useState(null);
  useEffect(() => {
    if (!open || !jobId) return;
    let active = true, attempts = 0;
    const poll = async () => {
      if (attempts++ > 120) return;
      try {
        const { data } = await api.get(`/events/whatsapp-bulk/${jobId}`);
        if (!active) return;
        setJob(data);
        if (data.status === "running") setTimeout(poll, 2000);
      } catch { if (active) setTimeout(poll, 3000); }
    };
    setJob(null);
    poll();
    return () => { active = false; };
  }, [open, jobId]);
  const done = job && job.status !== "running";
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="comm-send-status-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {done ? <CheckCircle2 className="h-5 w-5 text-emerald-600" /> : <Loader2 className="h-5 w-5 animate-spin text-blue-600" />}
            {done ? "Verzonden" : "Bezig met versturen…"}
          </DialogTitle>
          <DialogDescription>{title}</DialogDescription>
        </DialogHeader>
        {job === null ? (
          <div className="py-4 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
        ) : (
          <div className="space-y-3">
            <BulkProgress job={job} channel={job.kind === "event_email" ? "email" : "whatsapp"} testId="comm-send-status-counts" />
            {done && <p className="text-xs text-slate-500">Het resultaat is ook gedeeld in het Slack-onboardingkanaal.</p>}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="comm-send-status-close">Sluiten</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const emptyStep = { offset_value: 7, offset_unit: "days", title: "", channel: "whatsapp", subject: "", message: "" };

function StepDialog({ eventId, step, open, onClose, onSaved, placeholders }) {
  const [f, setF] = useState(emptyStep);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) setF(step ? {
      offset_value: step.offset_value, offset_unit: step.offset_unit, title: step.title || "",
      channel: step.channel, subject: step.subject || "",
      message: step.channel === "email" ? toEditorHtml(step.message || "") : (step.message || ""),
    } : emptyStep);
  }, [open, step]);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const setField = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async () => {
    const emptyMsg = f.channel === "email" ? isQuillEmpty(f.message) : !f.message.trim();
    if (!f.title.trim() || emptyMsg) { toast.error("Titel en inhoud zijn verplicht"); return; }
    if (f.channel === "email" && !f.subject.trim()) { toast.error("Onderwerp is verplicht bij e-mail"); return; }
    setSaving(true);
    try {
      const body = { ...f, offset_value: Number(f.offset_value) || 0 };
      if (step) await api.put(`/events/${eventId}/comm-flow/${step.id}`, body);
      else await api.post(`/events/${eventId}/comm-flow`, body);
      toast.success(step ? "Bericht bijgewerkt" : "Bericht toegevoegd aan de communicatieflow 🎉");
      onSaved(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Opslaan mislukt"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="comm-step-dialog">
        <DialogHeader>
          <DialogTitle>{step ? "Bericht bewerken" : "Nieuw bericht in de communicatieflow"}</DialogTitle>
          <DialogDescription>Wordt op het gekozen moment klaargezet ter goedkeuring en gaat alléén naar aangemelde deelnemers (incl. team en genodigden, minus afmeldingen).</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="space-y-1.5"><Label className="text-xs">Moment vóór start *</Label>
              <div className="flex gap-2">
                <Input type="number" min="0" value={f.offset_value} onChange={set("offset_value")}
                  className="w-20" data-testid="comm-step-offset" />
                <Select value={f.offset_unit} onValueChange={setField("offset_unit")}>
                  <SelectTrigger className="flex-1" data-testid="comm-step-unit"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="days">dagen</SelectItem>
                    <SelectItem value="hours">uren</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5"><Label className="text-xs">Titel template/bericht *</Label>
              <Input value={f.title} onChange={set("title")} placeholder="Laatste info" data-testid="comm-step-title" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Kanaal *</Label>
              <Select value={f.channel} onValueChange={setField("channel")}>
                <SelectTrigger data-testid="comm-step-channel"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="whatsapp">WhatsApp</SelectItem>
                  <SelectItem value="email">E-mail</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {f.channel === "email" ? (
            <EmailFields subject={f.subject} setSubject={setField("subject")}
              body={f.message} setBody={setField("message")}
              tags={placeholders || []} minHeight={160} testId="comm-step"
              subjectPlaceholder="Laatste info voor {eventnaam}"
              placeholder={"Hoi {voornaam},\n\nOver een week is het zover…"} />
          ) : (
            <WhatsAppFields message={f.message} setMessage={setField("message")}
              tags={placeholders || []} rows={7} testId="comm-step" label="Inhoud *"
              placeholder={"Hoi {voornaam},\n\nOver een week is het zover: {eventnaam} op {eventdatum} om {eventstarttijd} in {eventlocatie} ({eventadres})!"} />
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuleren</Button>
          <Button onClick={submit} disabled={saving} className="bg-blue-700 hover:bg-blue-800 gap-1.5" data-testid="comm-step-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Opslaan
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function CommFlow({ eventId, eventName }) {
  const [data, setData] = useState(null);
  const [dialog, setDialog] = useState(null); // { step } | "new"
  const [statusJob, setStatusJob] = useState(null); // { jobId, title }
  const [confirmSend, setConfirmSend] = useState(null); // step
  const [testBusy, setTestBusy] = useState(null);
  const sendTest = async (s, toEmail) => {
    setTestBusy(s.id);
    try {
      const { data } = await api.post(`/events/comm-steps/${s.id}/test`, { to_email: toEmail });
      if (data.ok) toast.success(data.message);
      else toast.warning(data.message, { duration: 8000 });
    } catch (e) { toast.error(e.response?.data?.detail || "Test versturen mislukt"); }
    finally { setTestBusy(null); }
  };
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/events/${eventId}/comm-flow`).then((r) => setData(r.data)).catch(() => setData({ steps: [], placeholders: [] }));
  }, [eventId]);
  useEffect(() => { setData(null); load(); }, [load]);

  const remove = async (step) => {
    if (!window.confirm(`"${step.title}" uit de flow verwijderen?`)) return;
    try { await api.delete(`/events/${eventId}/comm-flow/${step.id}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Verwijderen mislukt"); }
  };

  const sendNow = async (step, force = false) => {
    setBusy(true);
    try {
      const { data: r } = await api.post(`/events/${eventId}/comm-flow/${step.id}/send-now`, null, { params: force ? { force: true } : {} });
      setConfirmSend(null);
      setStatusJob({ jobId: r.job_id, title: `${step.title} — ${r.total} aangemelde deelnemers` });
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && detail) {
        // Tijd-waakhond: stap staat ver in de toekomst gepland → expliciete bevestiging
        if (window.confirm(`${detail}\n\nWeet je zeker dat je dit NU wilt versturen?`)) { sendNow(step, true); return; }
        setConfirmSend(null);
      } else if (detail) {
        toast.error(typeof detail === "string" ? detail : "Versturen mislukt");
      } else {
        // Verbinding weggevallen: check of de verzending tóch gestart is (voorkomt dubbel versturen)
        let recovered = false;
        try {
          const kind = step.channel === "whatsapp" ? "event_whatsapp" : "event_email";
          const { data } = await api.get(`/events/${eventId}/bulk-jobs/latest`, { params: { kind } });
          const j = data.job;
          if (j && Date.now() - new Date(j.created_at).getTime() < 3 * 60 * 1000) {
            toast.info("De verbinding viel even weg, maar de verzending is wél gestart — hier is de status.");
            setConfirmSend(null);
            setStatusJob({ jobId: j.id, title: `${step.title} — ${j.total} aangemelde deelnemers` });
            load();
            recovered = true;
          }
        } catch { /* geen herstel-info beschikbaar */ }
        if (!recovered) toast.error("Server tijdelijk onbereikbaar — er is niets verstuurd. Probeer het over een minuut opnieuw.");
      }
    } finally { setBusy(false); }
  };

  const decide = async (step, send) => {
    setBusy(true);
    try {
      const { data: r } = await api.post(`/events/comm-approvals/${step.prompt_id}/decide`, { send });
      if (send && r.job_id) setStatusJob({ jobId: r.job_id, title: step.title });
      else toast.success(send ? "Wordt verstuurd" : "Niet verstuurd");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Mislukt"); }
    finally { setBusy(false); }
  };

  return (
    <Card className="p-5 border-slate-200" data-testid="comm-flow-card">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
        <div className="flex items-center gap-2">
          <Send className="h-4 w-4 text-blue-700" />
          <h3 className="font-heading text-base font-semibold text-slate-800">Communicatieflow</h3>
          {data && <span className="text-xs text-slate-400">{data.recipients_count} aangemelde ontvangers · start {data.start_time} uur</span>}
        </div>
        <Button size="sm" variant="outline" onClick={() => setDialog("new")} className="gap-1.5 border-blue-200 text-blue-700 hover:bg-blue-50" data-testid="comm-add-step-btn">
          <Plus className="h-3.5 w-3.5" /> Nieuw bericht
        </Button>
      </div>
      <p className="text-xs text-slate-500 mb-3">Standaardberichten op vaste momenten vóór het event. Op het moment zelf krijgen Wouter en Charissa een popup + Slack-DM ter goedkeuring — er wordt nooit ongevraagd verstuurd.</p>
      {data === null ? (
        <div className="py-4 text-center text-slate-400"><Loader2 className="h-4 w-4 animate-spin inline" /></div>
      ) : data.steps.length === 0 ? (
        <p className="text-sm text-slate-400 py-2" data-testid="comm-flow-empty">Nog geen berichten. Voeg bijvoorbeeld "Over een week is het zover" (7 dagen) of "Laatste info + adres" (1 dag) toe.</p>
      ) : (
        <div className="space-y-1.5" data-testid="comm-flow-steps">
          {data.steps.map((s) => {
            const st = STATUS[s.status] || STATUS.scheduled;
            return (
              <div key={s.id} data-testid={`comm-step-${s.id}`} className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-100 px-3 py-2">
                <span className="inline-flex items-center gap-1 text-xs text-slate-500 w-36 shrink-0">
                  <Clock className="h-3.5 w-3.5 text-slate-400" />
                  {s.offset_value} {s.offset_unit === "hours" ? "uur" : s.offset_value === 1 ? "dag" : "dagen"} voor start
                </span>
                {s.channel === "whatsapp"
                  ? <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700"><MessageSquare className="h-3 w-3" /> WhatsApp</span>
                  : <span className="inline-flex items-center gap-1 rounded-full border border-sky-200 bg-sky-50 px-2 py-0.5 text-[10px] font-semibold text-sky-700"><Mail className="h-3 w-3" /> E-mail</span>}
                <span className="text-sm font-medium text-slate-700 flex-1 min-w-[10rem] truncate" title={s.message}>{s.title}</span>
                <span className="text-[11px] text-slate-400">{s.status === "sent" ? `verzonden ${fmtDue(s.sent_at)}` : `moment: ${fmtDue(s.due_at)}`}</span>
                <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold ${st.cls}`} data-testid={`comm-step-status-${s.id}`}>{st.label}</span>
                {s.status === "awaiting_approval" && s.prompt_id && (
                  <span className="inline-flex gap-1">
                    <Button size="sm" disabled={busy} onClick={() => decide(s, true)} className="h-6 px-2 text-[11px] bg-emerald-600 hover:bg-emerald-700" data-testid={`comm-approve-${s.id}`}>Verzenden</Button>
                    <Button size="sm" variant="outline" disabled={busy} onClick={() => decide(s, false)} className="h-6 px-2 text-[11px] text-red-600 border-red-200 hover:bg-red-50" data-testid={`comm-skip-${s.id}`}>Niet verzenden</Button>
                  </span>
                )}
                {s.status !== "sent" && (
                  <span className="inline-flex gap-0.5">
                    <TestSendMenu testId={`comm-test-menu-${s.id}`} onPick={(email) => sendTest(s, email)} trigger={
                      <button disabled={testBusy === s.id}
                        title={s.channel === "whatsapp" ? "Stuur een test-WhatsApp" : "Stuur een test-e-mail"}
                        data-testid={`comm-test-${s.id}`} className="p-1 text-slate-300 hover:text-emerald-600">
                        {testBusy === s.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : s.channel === "whatsapp" ? <Smartphone className="h-3.5 w-3.5" /> : <MailCheck className="h-3.5 w-3.5" />}
                      </button>} />
                    <button onClick={() => setConfirmSend(s)} title="Nu direct versturen" data-testid={`comm-sendnow-${s.id}`}
                      className="p-1 text-slate-300 hover:text-blue-600"><Send className="h-3.5 w-3.5" /></button>
                    <button onClick={() => setDialog({ step: s })} title="Bewerken" data-testid={`comm-edit-${s.id}`}
                      className="p-1 text-slate-300 hover:text-slate-600"><Pencil className="h-3.5 w-3.5" /></button>
                    <button onClick={() => remove(s)} title="Verwijderen" data-testid={`comm-delete-${s.id}`}
                      className="p-1 text-slate-300 hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
      <StepDialog eventId={eventId} step={dialog && dialog !== "new" ? dialog.step : null}
        open={!!dialog} onClose={() => setDialog(null)} onSaved={load} placeholders={data?.placeholders} />
      <Dialog open={!!confirmSend} onOpenChange={(o) => !o && setConfirmSend(null)}>
        <DialogContent className="sm:max-w-md" data-testid="comm-confirm-send-dialog">
          <DialogHeader>
            <DialogTitle>Nu versturen?</DialogTitle>
            <DialogDescription>
              "{confirmSend?.title}" wordt direct per {confirmSend?.channel === "whatsapp" ? "WhatsApp" : "e-mail"} verstuurd
              naar {data?.recipients_count} aangemelde deelnemers van {eventName}.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmSend(null)}>Annuleren</Button>
            <Button onClick={() => sendNow(confirmSend)} disabled={busy} className="bg-blue-700 hover:bg-blue-800 gap-1.5" data-testid="comm-confirm-send-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Versturen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <SendStatusDialog jobId={statusJob?.jobId} title={statusJob?.title} open={!!statusJob} onClose={() => setStatusJob(null)} />
    </Card>
  );
}
